import { isBinocularTest, isFixationChanging, isSimpleTest } from "@utils/TestUtils";
import { FEATURE_VERSION_MAP } from "@constants/testConstants/versionConstants";
import { compareVersions, validate } from "compare-versions";
import {
  VirtualAssistantConfig,
  AssistanceVideo,
  InteractionType,
  TestCategory,
  TestType,
  TestConfiguration,
  CPTCustomConfiguration,
  OMTestCustomConfiguration,
  OnVideoCompleteAction,
  VFATestCustomConfiguration,
  TestLanguage,
} from "../types/testTypes";

const CPT_IDENTICAL_PAIRS_CLICK_FIX_VERSION = "2.8.6";

const COMMON_VIDEO_PREFIX = "common/";

export const VA_AUDIO_NAMES = [
  "VFA/fixation_loss_warning.wav",
  "common/focus_on_point_and_click_01.wav",
];

// Default empty virtual assistant config (No Assistance)
export const defaultVirtualAssistantConfig: VirtualAssistantConfig = {
  showAssistant: false,
  shouldPlayButtonClickSound: false,
  assistantLanguage: TestLanguage.English,
  testName: "UNKNOWN_TEST",
  videoInfos: [],
};

export const eyeTrackingCalibrationInstructionsVideo: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "common/eyeTrackingcalibration_instructions",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const commonTriggerErrorVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "common/trigger_error_01",
  timeOutInSeconds: 5.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const commonTriggerVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "common/trigger_01",
  timeOutInSeconds: 5.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: commonTriggerErrorVideo01,
  errorVideoLoopCount: 2,
  shouldAppearOnce: true,
};

const commonCalibrationErrorVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Focus,
  videoName: "common/calibration_error_01",
  timeOutInSeconds: 5.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const commonAdultCalibrationVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Focus,
  videoName: "common/adult_calibration_01",
  timeOutInSeconds: 5.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: commonCalibrationErrorVideo01,
  errorVideoLoopCount: 2,
  shouldAppearOnce: true,
};

const commmonFocusOnPointAndClickVideo: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Focus,
  videoName: "common/focus_on_point_and_click_01",
  timeOutInSeconds: 0.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
  shouldAppearOnce: false,
};

const commonOnlyClickVideo: AssistanceVideo = {
  order: 2,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Focus,
  videoName: "common/only_click_01",
  timeOutInSeconds: 0.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
  shouldAppearOnce: false,
};

const VIDEOS_SUPERSEDED_BY_ASSISTANT_AUDIO: string[] = [
  commonTriggerVideo01.videoName,
  commmonFocusOnPointAndClickVideo.videoName,
  commonOnlyClickVideo.videoName,
];

const commonChildCalibrationVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Focus,
  videoName: "common/child_calibration_01",
  timeOutInSeconds: 5.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: commonCalibrationErrorVideo01,
  errorVideoLoopCount: 2,
  shouldAppearOnce: true,
};

const commonTestStartingVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Focus,
  videoName: "common/test_starting_01",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const adultGoNoGoIstructionsErrorVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "CPT/adult/go_no_go/instructions_error_01",
  timeOutInSeconds: 3.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const adultGoNoGoInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "CPT/adult/go_no_go/instructions_01",
  timeOutInSeconds: 3.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: adultGoNoGoIstructionsErrorVideo01,
  errorVideoLoopCount: 2,
};

const adultIdenticalPairsInstructionsErrorVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "CPT/adult/identical_pairs/instructions_error_01",
  timeOutInSeconds: 3.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const adultIdenticalPairsInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "CPT/adult/identical_pairs/instructions_01",
  timeOutInSeconds: 3.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: adultIdenticalPairsInstructionsErrorVideo01,
  errorVideoLoopCount: 2,
};

const childGoNoGoInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "CPT/child/go_no_go/instructions_01",
  timeOutInSeconds: 3.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const childIdenticalPairsInstructionsErrorVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "CPT/adult/identical_pairs/instructions_error_01",
  timeOutInSeconds: 3.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const childIdenticalPairsInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "CPT/child/identical_pairs/instructions_01",
  timeOutInSeconds: 3.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: childIdenticalPairsInstructionsErrorVideo01,
  errorVideoLoopCount: 2,
};

const childIdenticalPairsInstructionsErrorVideo02: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "CPT/adult/identical_pairs/instructions_error_01",
  timeOutInSeconds: 3.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const childIdenticalPairsInstructionsVideo02: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "CPT/child/identical_pairs/instructions_02",
  timeOutInSeconds: 3.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: childIdenticalPairsInstructionsErrorVideo02,
  errorVideoLoopCount: 2,
};

const dyslexiaAloudReadingInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "DY/aloud/instructions_01",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const dyslexiaSilentReadingInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "DY/silent/instructions_01",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const readingAssessmentInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.Click,
  videoName: "RA/instructions_01",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const commonWelcomeVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "common/welcome_01",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
  shouldAppearOnce: true,
};

const commonWelcomeVideo02: AssistanceVideo = {
  order: 2,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "common/welcome_02",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
  shouldAppearOnce: true,
};

const vfaNormalInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "VFA/normal/instructions_01",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const vfaNormalInstructionsVideo03: AssistanceVideo = {
  order: 3,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "VFA/normal/instructions_03",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const vfaFoveaInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "VFA/fovea/instructions_01",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const vfaFoveaInstructionsVideo02: AssistanceVideo = {
  order: 2,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "VFA/fovea/instructions_02",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const vfaFoveaInstructionsVideo03: AssistanceVideo = {
  order: 3,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "VFA/fovea/instructions_03",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const vfaChangingFixationInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "VFA/changing_fixation/instructions_01",
  timeOutInSeconds: 0.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
  onCompleteAction: OnVideoCompleteAction.PlayDingSound,
};

const vfaChangingFixationInstructionsVideo02: AssistanceVideo = {
  order: 2,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "VFA/changing_fixation/instructions_02",
  timeOutInSeconds: 2.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const ocularMotilityInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "OM/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const smoothPursuitChildInstructionsVideo01: AssistanceVideo = {
  ...ocularMotilityInstructionsVideo01,
  videoName: "OM/child/smooth_pursuit_instructions_01",
};

const pupillomertyInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "PL/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const coverTestInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "COV/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const objectivePhoriaInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "COV/phoria_instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const contrastSensitivityThresholdInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "CS/threshold/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const contrastSensitivityAscendingInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "CS/ascending/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const contrastSensitivityPelliRobsonInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "CS/pelli_robinson/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const visualAcuityTumblingEInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "VA/tumbling_e/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const colorVisionInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "CV/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const colorVisionAutomaticInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "CV/auto_instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const stereoAcuityInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "SA/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const kineticInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "KIN/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const fdtInstructionVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "FDT/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const dcInstructionVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "DC/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const wcstInstructionVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "WCST/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const optokineticNystagmusInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "OKN/instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const npcInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "BVT/npc_instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

export const antiSaccadesInstructionsVideo01: AssistanceVideo = {
  order: 1,
  awaitedInteractionToStart: InteractionType.None,
  awaitedInteractionAtComplete: InteractionType.None,
  videoName: "OM/antisaccade_instructions_01",
  timeOutInSeconds: 1.0,
  onCompleteInteractionSuccess: null,
  onCompleteInteractionFail: null,
  errorVideoLoopCount: 2,
};

const commonWelcomeVideos: AssistanceVideo[] = [commonWelcomeVideo01, commonWelcomeVideo02];
const commonTriggerTutorialVideos: AssistanceVideo[] = [commonTriggerVideo01];

export const virtualAssistanceCPTAdultGoNoGoConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  testName: "CPT",
  shouldPlayButtonClickSound: false,
  videoInfos: [
    { ...commonAdultCalibrationVideo01, order: 1 },
    { ...adultGoNoGoInstructionsVideo01, order: 2 },
    { ...commonTestStartingVideo01, order: 3 },
  ],
};

export const virtualAssistanceCPTAdultIdenticalPairsConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: false,
  testName: "CPT",
  videoInfos: [
    { ...commonAdultCalibrationVideo01, order: 1 },
    { ...adultIdenticalPairsInstructionsVideo01, order: 2 },
    { ...commonTestStartingVideo01, order: 3 },
  ],
};

export const virtualAssistanceCPTChildGoNoGoConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: false,
  testName: "CPT",
  videoInfos: [
    { ...commonChildCalibrationVideo01, order: 1 },
    { ...childGoNoGoInstructionsVideo01, order: 2 },
    { ...commonTestStartingVideo01, order: 3 },
  ],
};

export const virtualAssistanceCPTChildIdenticalPairsConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: false,
  testName: "CPT",
  videoInfos: [
    { ...commonChildCalibrationVideo01, order: 1 },
    { ...childIdenticalPairsInstructionsVideo01, order: 2 },
    { ...childIdenticalPairsInstructionsVideo02, order: 3 },
    { ...commonTestStartingVideo01, order: 4 },
  ],
};

export const virtualAssistanceDyslexiaAloudReadingConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: "DY",
  videoInfos: [
    { ...commonChildCalibrationVideo01, order: 1 },
    { ...commonTriggerVideo01, order: 2 },
    { ...dyslexiaAloudReadingInstructionsVideo01, order: 3 },
  ],
};

export const virtualAssistanceDyslexiaSilentReadingConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: "DY",
  videoInfos: [
    { ...commonChildCalibrationVideo01, order: 1 },
    { ...commonTriggerVideo01, order: 2 },
    { ...dyslexiaSilentReadingInstructionsVideo01, order: 3 },
  ],
};

export const virtualAssistanceReadingAssessmentConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: "RA",
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...readingAssessmentInstructionsVideo01, order: 4 },
    { ...commonOnlyClickVideo, order: 5 },
  ],
};

export const virtualAssistanceFDTConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.FDT,
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...fdtInstructionVideo01, order: 4 },
    { ...commmonFocusOnPointAndClickVideo, order: 5 },
  ],
};

export const virtualAssistanceDyscalculiaConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.Dyscalculia,
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...dcInstructionVideo01, order: 4 },
    { ...commonOnlyClickVideo, order: 5 },
  ],
};

export const virtualAssistanceWCSTConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.WisconsinCardSorting,
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...wcstInstructionVideo01, order: 4 },
    { ...commonOnlyClickVideo, order: 5 },
  ],
};

export const virtualAssistanceVFANoFoveaNoChangingFixationConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.VisualField,
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...vfaNormalInstructionsVideo01, order: 4 },
    { ...vfaNormalInstructionsVideo03, order: 6 },
    { ...commmonFocusOnPointAndClickVideo, order: 7 },
  ],
};

export const virtualAssistanceVFAFoveaOnlyConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.VisualField,
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...vfaFoveaInstructionsVideo01, order: 4 },
    { ...vfaFoveaInstructionsVideo02, order: 5 },
    { ...vfaFoveaInstructionsVideo03, order: 6 },
    { ...vfaNormalInstructionsVideo01, order: 7 },
    { ...vfaNormalInstructionsVideo03, order: 9 },
    { ...commmonFocusOnPointAndClickVideo, order: 10 },
  ],
};

export const virtualAssistanceVFAChangingFixationOnlyConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.VisualField,
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...vfaNormalInstructionsVideo01, order: 4 },
    { ...vfaNormalInstructionsVideo03, order: 6 },
    { ...vfaChangingFixationInstructionsVideo01, order: 7 },
    { ...vfaChangingFixationInstructionsVideo02, order: 8 },
    { ...commmonFocusOnPointAndClickVideo, order: 7 },
  ],
};

export const virtualAssistanceVFAAllConditionsConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.VisualField,
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...vfaFoveaInstructionsVideo01, order: 4 },
    { ...vfaFoveaInstructionsVideo02, order: 5 },
    { ...vfaFoveaInstructionsVideo03, order: 6 },
    { ...vfaNormalInstructionsVideo01, order: 7 },
    { ...vfaNormalInstructionsVideo03, order: 9 },
    { ...vfaChangingFixationInstructionsVideo01, order: 10 },
    { ...vfaChangingFixationInstructionsVideo02, order: 11 },
    { ...commmonFocusOnPointAndClickVideo, order: 12 },
  ],
};

export const virtualAssistanceOcularMotilityConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.OcularMotility,
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...ocularMotilityInstructionsVideo01, order: 4 },
    { ...commmonFocusOnPointAndClickVideo, order: 7 },
  ],
};

export const virtualAssistancePupillometryConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.Pupillometry,
  videoInfos: [
    ...commonWelcomeVideos,
    { ...pupillomertyInstructionsVideo01, order: 4 },
    { ...commmonFocusOnPointAndClickVideo, order: 7 },
  ],
};

export const virtualAssistanceCoverTestConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.Cover,
  videoInfos: [
    ...commonWelcomeVideos,
    { ...coverTestInstructionsVideo01, order: 4 },
    { ...commmonFocusOnPointAndClickVideo, order: 7 },
  ],
};

export const virtualAssistanceObjectivePhoriaConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.Cover,
  videoInfos: [
    ...commonWelcomeVideos,
    { ...objectivePhoriaInstructionsVideo01, order: 4 },
    { ...commmonFocusOnPointAndClickVideo, order: 7 },
  ],
};

export const virtualAssistanceContrastSensitivityThresholdConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.ContrastSensitivity,
  videoInfos: [
    ...commonWelcomeVideos,
    { ...contrastSensitivityThresholdInstructionsVideo01, order: 4 },
    commonOnlyClickVideo,
  ],
};

export const virtualAssistanceContrastSensitivityAscendingConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.ContrastSensitivity,
  videoInfos: [
    ...commonWelcomeVideos,
    { ...contrastSensitivityAscendingInstructionsVideo01, order: 4 },
    commonOnlyClickVideo,
  ],
};

export const virtualAssistanceContrastSensitivityPelliRobsonConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.ContrastSensitivity,
  videoInfos: [
    ...commonWelcomeVideos,
    { ...contrastSensitivityPelliRobsonInstructionsVideo01, order: 4 },
  ],
};

export const virutalAssistanceVisualAcuityTumblingEConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.VisualAcuity,
  videoInfos: [...commonWelcomeVideos, { ...visualAcuityTumblingEInstructionsVideo01, order: 4 }],
};

export const virtualAssistanceVisualAcuityLogmarConfig: VirtualAssistantConfig = {
  ...virtualAssistanceContrastSensitivityPelliRobsonConfig,
  testName: TestCategory.VisualAcuity,
};

export const virtualAssistanceColorVisionConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.ColorVision,
  videoInfos: [...commonWelcomeVideos, { ...colorVisionInstructionsVideo01, order: 4 }],
};

export const virtualAssistanceColorVisionAutomaticConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.ColorVision,
  videoInfos: [...commonWelcomeVideos, { ...colorVisionAutomaticInstructionsVideo01, order: 4 }],
};

export const virtualAssistanceStereoAcuityConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.StereoAcuity,
  videoInfos: [...commonWelcomeVideos, { ...stereoAcuityInstructionsVideo01, order: 4 }],
};

export const virtualAssistantVideonystagmographyConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: "VSG",
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    commmonFocusOnPointAndClickVideo,
  ],
};

export const virtualAssistantKineticConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: "KIN",
  videoInfos: [
    ...commonWelcomeVideos,
    { ...kineticInstructionsVideo01, order: 4 },
    commonOnlyClickVideo,
  ],
};

export const virtualAssistanceOptokineticNystagmusConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.OptokineticNystagmus,
  videoInfos: [...commonWelcomeVideos, { ...optokineticNystagmusInstructionsVideo01, order: 4 }],
};

export const virtualAssistanceNPCConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.BinocularVision,
  videoInfos: [...commonWelcomeVideos, { ...npcInstructionsVideo01, order: 4 }],
};

export const virtualAssistanceAntiSaccadesConfig: VirtualAssistantConfig = {
  showAssistant: true,
  assistantLanguage: TestLanguage.English,
  shouldPlayButtonClickSound: true,
  testName: TestCategory.OcularMotility,
  videoInfos: [
    ...commonWelcomeVideos,
    ...commonTriggerTutorialVideos,
    { ...antiSaccadesInstructionsVideo01, order: 4 },
    { ...commmonFocusOnPointAndClickVideo, order: 7 },
  ],
};

// The child flow is the standard ocular motility flow with only the
// instruction video swapped, so it stays in sync by construction.
export const virtualAssistanceSmoothPursuitChildConfig: VirtualAssistantConfig = {
  ...virtualAssistanceOcularMotilityConfig,
  videoInfos: virtualAssistanceOcularMotilityConfig.videoInfos.map((video) =>
    video.videoName === ocularMotilityInstructionsVideo01.videoName
      ? { ...smoothPursuitChildInstructionsVideo01, order: video.order }
      : video,
  ),
};

export function getVirtualAssistantVideoListForTest(
  testConfiguration: TestConfiguration,
  doNotShowAgainList?: string[],
  headsetVersion?: string | null,
): AssistanceVideo[] {
  if (!testConfiguration) {
    throw new Error("Test configuration is undefined");
  }
  const name: TestCategory = testConfiguration.name;
  const testType: TestType = testConfiguration.testType;
  if (!name || !testType) {
    return [];
  }
  let videoList: AssistanceVideo[] = [];

  switch (name as TestCategory) {
    case TestCategory.VisualField: {
      const extraData = testConfiguration.extraData as VFATestCustomConfiguration;
      const shouldIncludeFixationInstructions = isFixationChanging(testType);
      const simpleTest = isSimpleTest(testType, extraData?.testBehaviour);
      const binocularTest = isBinocularTest(testType);
      const shouldIncludeFoveaInstructions =
        simpleTest || binocularTest ? false : extraData?.fovealThresholdTestingEnabled;

      if (shouldIncludeFoveaInstructions && shouldIncludeFixationInstructions) {
        videoList = virtualAssistanceVFAAllConditionsConfig.videoInfos;
      } else if (shouldIncludeFoveaInstructions) {
        videoList = virtualAssistanceVFAFoveaOnlyConfig.videoInfos;
      } else if (shouldIncludeFixationInstructions) {
        videoList = virtualAssistanceVFAChangingFixationOnlyConfig.videoInfos;
      } else {
        videoList = virtualAssistanceVFANoFoveaNoChangingFixationConfig.videoInfos;
      }
      break;
    }
    case TestCategory.FDT: {
      videoList = virtualAssistanceFDTConfig.videoInfos;
      break;
    }
    case TestCategory.ContinuousPerformance: {
      const extraData = testConfiguration.extraData as CPTCustomConfiguration;
      switch (testType) {
        case TestType.GoNoGo:
          videoList = extraData?.isForAdult
            ? virtualAssistanceCPTAdultGoNoGoConfig.videoInfos
            : virtualAssistanceCPTChildGoNoGoConfig.videoInfos;

          break;
        case TestType.IdenticalPairs:
          videoList = extraData?.isForAdult
            ? virtualAssistanceCPTAdultIdenticalPairsConfig.videoInfos
            : virtualAssistanceCPTChildIdenticalPairsConfig.videoInfos;
          break;
      }
      break;
    }
    case TestCategory.Dyslexia:
      videoList = virtualAssistanceDyslexiaAloudReadingConfig.videoInfos;
      break;
    case TestCategory.ReadingAssessment:
      videoList = virtualAssistanceReadingAssessmentConfig.videoInfos;
      break;
    case TestCategory.OcularMotility: {
      const omExtraData = testConfiguration.extraData as OMTestCustomConfiguration;
      if (testType === TestType.AntiSaccades) {
        videoList = virtualAssistanceAntiSaccadesConfig.videoInfos;
      } else if (testType === TestType.SmoothPursuit && omExtraData?.childMode) {
        videoList = virtualAssistanceSmoothPursuitChildConfig.videoInfos;
      } else {
        videoList = virtualAssistanceOcularMotilityConfig.videoInfos;
      }
      break;
    }
    case TestCategory.Pupillometry:
      videoList = virtualAssistancePupillometryConfig.videoInfos;
      break;
    case TestCategory.Cover:
      switch (testType) {
        case TestType.ObjectivePhoria:
          videoList = virtualAssistanceObjectivePhoriaConfig.videoInfos;
          break;
        default:
          videoList = virtualAssistanceCoverTestConfig.videoInfos;
          break;
      }
      break;
    case TestCategory.ContrastSensitivity:
      switch (testType) {
        case TestType.Threshold:
          videoList = virtualAssistanceContrastSensitivityThresholdConfig.videoInfos;
          break;
        case TestType.Ascending:
          videoList = virtualAssistanceContrastSensitivityAscendingConfig.videoInfos;
          break;
        case TestType.PelliRobson:
          videoList = virtualAssistanceContrastSensitivityPelliRobsonConfig.videoInfos;
          break;
      }
      break;
    case TestCategory.VisualAcuity:
      switch (testType) {
        case TestType.TumblingE:
        case TestType.TumblingEThreshold:
          videoList = virutalAssistanceVisualAcuityTumblingEConfig.videoInfos;
          break;
        case TestType.Logmar:
          videoList = virtualAssistanceVisualAcuityLogmarConfig.videoInfos;
          break;
      }
      break;
    case TestCategory.ColorVision:
      switch (testType) {
        case TestType.ColorVisionAutomatic:
          videoList = virtualAssistanceColorVisionAutomaticConfig.videoInfos;
          break;
        default:
          videoList = virtualAssistanceColorVisionConfig.videoInfos;
          break;
      }
      break;
    case TestCategory.StereoAcuity:
      videoList = virtualAssistanceStereoAcuityConfig.videoInfos;
      break;
    case TestCategory.Videonystagmography:
      videoList = virtualAssistantVideonystagmographyConfig.videoInfos;
      break;
    case TestCategory.Kinetic:
      videoList = virtualAssistantKineticConfig.videoInfos;
      break;
    case TestCategory.WisconsinCardSorting:
      videoList = virtualAssistanceWCSTConfig.videoInfos;
      break;
    case TestCategory.Dyscalculia:
      videoList = virtualAssistanceDyscalculiaConfig.videoInfos;
      break;
    case TestCategory.OptokineticNystagmus:
      videoList = virtualAssistanceOptokineticNystagmusConfig.videoInfos;
      break;
    case TestCategory.BinocularVision:
      videoList = virtualAssistanceNPCConfig.videoInfos;
      break;
  }
  if (name === TestCategory.ContinuousPerformance && testType === TestType.IdenticalPairs) {
    const awaitedInteractionAtComplete = getCPTIdenticalPairsCompletionInteraction(headsetVersion);
    videoList = videoList.map((video) =>
      video.videoName.includes("/identical_pairs/instructions_")
        ? { ...video, awaitedInteractionAtComplete }
        : video,
    );
  }
  if (
    testConfiguration.startWithCalibrationScene &&
    supportsEyeTrackingCalibrationInstructions(headsetVersion)
  ) {
    videoList = [eyeTrackingCalibrationInstructionsVideo, ...videoList];
  }
  if (
    name === TestCategory.VisualField &&
    testConfiguration.virtualAssistanceConfig?.quickAssistant &&
    supportsAssistantAudio(headsetVersion)
  ) {
    videoList = videoList.filter(
      (video) =>
        video.videoName !== vfaNormalInstructionsVideo01.videoName &&
        !video.videoName.startsWith(COMMON_VIDEO_PREFIX),
    );
  }
  if (supportsAssistantAudio(headsetVersion)) {
    videoList = videoList.filter(
      (video) => !VIDEOS_SUPERSEDED_BY_ASSISTANT_AUDIO.includes(video.videoName),
    );
  }
  videoList = filterOutDoNotShowAgainVideos(videoList, doNotShowAgainList);
  videoList = reOrderVideos(videoList);
  return videoList;
}

export function supportsAssistantAudio(headsetVersion?: string | null): boolean {
  return (
    typeof headsetVersion === "string" &&
    validate(headsetVersion) &&
    compareVersions(headsetVersion, FEATURE_VERSION_MAP.assistantAudio) >= 0
  );
}

export function getCPTIdenticalPairsCompletionInteraction(
  headsetVersion?: string | null,
): InteractionType {
  return typeof headsetVersion === "string" &&
    validate(headsetVersion) &&
    compareVersions(headsetVersion, CPT_IDENTICAL_PAIRS_CLICK_FIX_VERSION) >= 0
    ? InteractionType.Click
    : InteractionType.None;
}

export function supportsEyeTrackingCalibrationInstructions(
  headsetVersion?: string | null,
): boolean {
  return (
    typeof headsetVersion === "string" &&
    validate(headsetVersion) &&
    compareVersions(headsetVersion, FEATURE_VERSION_MAP.eyeTrackingCalibrationVaInstructions) >= 0
  );
}

function reOrderVideos(videoInfos: AssistanceVideo[]): AssistanceVideo[] {
  const reorderedVideos = videoInfos.map((video, index) => ({
    ...video,
    order: index + 1,
  }));
  return reorderedVideos;
}

const EQUIVALENT_VIDEO_GROUPS: string[][] = [
  ["common/adult_calibration_01", "common/child_calibration_01", "common/welcome_01"],
];

function expandWithEquivalents(doNotShowAgainList: string[]): string[] {
  const expanded = new Set(doNotShowAgainList);
  for (const group of EQUIVALENT_VIDEO_GROUPS) {
    if (group.some((name) => expanded.has(name))) {
      group.forEach((name) => expanded.add(name));
    }
  }
  return Array.from(expanded);
}

function filterOutDoNotShowAgainVideos(
  videoInfos: AssistanceVideo[],
  doNotShowAgainList?: string[],
): AssistanceVideo[] {
  if (!doNotShowAgainList || doNotShowAgainList.length === 0) {
    return videoInfos;
  }
  const effectiveList = expandWithEquivalents(doNotShowAgainList);
  return videoInfos.filter((video) => !effectiveList.includes(video.videoName));
}

export function getVideoNamesForDoNotShowAgainList(
  virtualAssistanceConfig: VirtualAssistantConfig,
): string[] {
  return virtualAssistanceConfig.videoInfos
    .filter((v) => v.shouldAppearOnce)
    .map((v) => v.videoName);
}
